import { DEBRIEF, SCORING } from '../config/tuning';
import type { MissionScore } from '../mission/Scoring';
import type { ObjectiveStatus } from '../mission/Objectives';
import type { FlightRecorder, TelemetryMark } from './Recorder';

/**
 * The debrief analysis (SPEC §3.12, M10).
 *
 * SPEC calls this "the single most valuable screen in the game and the reason the recorder
 * exists". The requirement is specific: a per-missile breakdown that "states in plain language
 * what the player did and why it worked or did not".
 *
 * That word *why* is the whole design. A list of miss distances is data; "you began the turn 1.2
 * seconds before impact, which is too late for the missile to be unable to correct" is a lesson.
 * So every judgement here is expressed as a rule with a threshold behind it, and the thresholds
 * live in config where they can be tuned against how the game actually plays.
 *
 * Distances in metres, times in seconds.
 */

export const EvasionVerdict = {
  /** Defeated it, and did so by flying well. */
  Clean: 'CLEAN',
  /** Defeated it, but by luck or distance rather than by a decision. */
  Incidental: 'INCIDENTAL',
  /** It hit. */
  Hit: 'HIT',
} as const;
export type EvasionVerdictId = (typeof EvasionVerdict)[keyof typeof EvasionVerdict];

export interface MissileAnalysis {
  readonly designation: string;
  readonly launchTimeS: number;
  readonly resolvedTimeS: number;
  readonly missDistanceM: number;
  readonly verdict: EvasionVerdictId;
  /** Plain-language explanation, the reason this screen exists. */
  readonly explanation: string;
  /** Seconds between launch and the first hard manoeuvre, or null if there was none. */
  readonly reactionS: number | null;
  /** Whether countermeasures were released between launch and resolution. */
  readonly usedCountermeasures: boolean;
}

export interface DebriefReport {
  readonly missionTitle: string;
  readonly success: boolean;
  readonly outcomeReason: string;
  readonly score: MissionScore | null;
  readonly objectives: readonly ObjectiveStatus[];
  readonly missiles: readonly MissileAnalysis[];
  readonly elapsedS: number;
  readonly meanIngressAltitudeM: number;
  readonly peakLoadFactor: number;
  /** Headline lessons, at most a handful, ordered by how much they cost. */
  readonly lessons: readonly string[];
}

/**
 * Explain one missile engagement.
 *
 * The judgement chain mirrors the physics of SPEC §3.6.5 and the model built at M6:
 *
 * 1. A *late* break cannot work — proportional navigation corrects any manoeuvre it has time for,
 *    so the only turn that defeats a command-guided round is one begun early enough to break the
 *    firing site's track before terminal guidance.
 * 2. A break *away* from the missile is worse than none: it holds the aspect the seeker wants and
 *    hands it a longer, straighter run.
 * 3. Chaff or flares without a turn is confetti — the decoy has to be paired with a geometry
 *    change or the seeker simply reacquires.
 * 4. A miss beyond `evasionLooseM` was never a threat, and the player should know that so they do
 *    not learn the wrong lesson from surviving.
 */
export function analyseMissile(params: {
  designation: string;
  launchTimeS: number;
  resolvedTimeS: number;
  missDistanceM: number;
  hit: boolean;
  peakLoadFactorDuring: number;
  reactionS: number | null;
  usedCountermeasures: boolean;
}): MissileAnalysis {
  const flightS = Math.max(0, params.resolvedTimeS - params.launchTimeS);
  const manoeuvred = params.peakLoadFactorDuring >= DEBRIEF.breakTurnG;
  const reactedLate =
    params.reactionS !== null && flightS - params.reactionS < DEBRIEF.lateBreakS;

  let verdict: EvasionVerdictId;
  let explanation: string;

  if (params.hit) {
    verdict = EvasionVerdict.Hit;
    if (!manoeuvred) {
      explanation =
        `It hit because you did not manoeuvre. A guided round flies to where you will be, ` +
        `not where you are — straight and level is the one flight path it can solve exactly.`;
    } else if (reactedLate) {
      const late = params.reactionS === null ? flightS : flightS - params.reactionS;
      explanation =
        `You broke, but ${late.toFixed(1)} s before impact. That is inside the missile's ` +
        `correction envelope: by then it has the energy and the turn rate to follow you. ` +
        `The break has to come while the round is still in mid-course.`;
    } else if (!params.usedCountermeasures) {
      explanation =
        `You broke in time, but nothing broke the site's track. A hard turn changes the ` +
        `geometry; chaff and a notch change what the radar can see. Against a command-guided ` +
        `round you need the second one.`;
    } else {
      explanation =
        `You broke and decoyed, and it still arrived. Check the aspect: a decoy only works if ` +
        `the aircraft moves out of the seeker's basket while it is distracted.`;
    }
  } else if (params.missDistanceM >= SCORING.evasionLooseM) {
    verdict = EvasionVerdict.Incidental;
    explanation =
      `It missed by ${Math.round(params.missDistanceM)} m — it was never going to reach you. ` +
      `Range, not flying, defeated this one; do not read it as a technique that worked.`;
  } else if (!manoeuvred) {
    verdict = EvasionVerdict.Incidental;
    explanation =
      `It missed by ${Math.round(params.missDistanceM)} m without you manoeuvring. That is luck ` +
      `— a guidance failure or a poor firing solution. The same shot will usually hit.`;
  } else {
    verdict = EvasionVerdict.Clean;
    const how = params.usedCountermeasures ? 'break turn and countermeasures' : 'break turn';
    explanation =
      `Defeated at ${Math.round(params.missDistanceM)} m by a ${how}. ` +
      `You began the turn with ${(flightS - (params.reactionS ?? 0)).toFixed(1)} s of missile ` +
      `flight left, which was enough to break the solution before terminal guidance.`;
  }

  return {
    designation: params.designation,
    launchTimeS: params.launchTimeS,
    resolvedTimeS: params.resolvedTimeS,
    missDistanceM: params.missDistanceM,
    verdict,
    explanation,
    reactionS: params.reactionS,
    usedCountermeasures: params.usedCountermeasures,
  };
}

/**
 * Pair launches with their resolutions and analyse each one.
 *
 * Marks arrive as a flat timeline, so launches and detonations have to be matched by designation
 * and order. A site that fires a salvo of two produces two launches before either resolves, which
 * is why this matches the *earliest unresolved* launch for a designation rather than the last one.
 */
export function analyseEngagements(recorder: FlightRecorder): MissileAnalysis[] {
  const pending = new Map<string, TelemetryMark[]>();
  const analyses: MissileAnalysis[] = [];

  for (let i = 0; i < recorder.marks.length; i += 1) {
    const mark = recorder.marks[i];
    if (mark.kind === 'launch') {
      const queue = pending.get(mark.detail) ?? [];
      queue.push(mark);
      pending.set(mark.detail, queue);
      continue;
    }
    if (mark.kind !== 'detonation') continue;

    const queue = pending.get(mark.detail);
    const launch = queue?.shift();
    if (launch === undefined) continue;

    analyses.push(
      analyseMissile({
        designation: mark.detail,
        launchTimeS: launch.timeS,
        resolvedTimeS: mark.timeS,
        missDistanceM: mark.value,
        hit: markSaysHit(recorder, mark),
        peakLoadFactorDuring: peakLoadBetween(recorder, launch.timeS, mark.timeS),
        reactionS: firstHardManoeuvre(recorder, launch.timeS, mark.timeS),
        usedCountermeasures: countermeasuresBetween(recorder, launch.timeS, mark.timeS),
      }),
    );
  }
  return analyses;
}

/** A detonation mark carries damage in `value`? No — miss distance. Damage rides the detail. */
function markSaysHit(recorder: FlightRecorder, mark: TelemetryMark): boolean {
  void recorder;
  return mark.value <= DEBRIEF.hitDistanceM;
}

function peakLoadBetween(recorder: FlightRecorder, fromS: number, toS: number): number {
  const sample = blankSample();
  let peak = 0;
  const start = recorder.indexAtTime(fromS);
  const end = recorder.indexAtTime(toS);
  for (let i = start; i <= end && i < recorder.count; i += 1) {
    recorder.read(i, sample);
    const n = Math.abs(sample.loadFactor);
    if (n > peak) peak = n;
  }
  return peak;
}

/** Seconds after launch at which the player first pulled hard, or null if they never did. */
function firstHardManoeuvre(recorder: FlightRecorder, fromS: number, toS: number): number | null {
  const sample = blankSample();
  const start = recorder.indexAtTime(fromS);
  const end = recorder.indexAtTime(toS);
  for (let i = start; i <= end && i < recorder.count; i += 1) {
    recorder.read(i, sample);
    if (Math.abs(sample.loadFactor) >= DEBRIEF.breakTurnG) return sample.timeS - fromS;
  }
  return null;
}

function countermeasuresBetween(recorder: FlightRecorder, fromS: number, toS: number): boolean {
  for (let i = 0; i < recorder.marks.length; i += 1) {
    const mark = recorder.marks[i];
    if (mark.timeS < fromS || mark.timeS > toS) continue;
    if (mark.kind === 'chaff' || mark.kind === 'flare') return true;
  }
  return false;
}

function blankSample(): ReturnType<FlightRecorder['read']> {
  return {
    timeS: 0,
    x: 0,
    y: 0,
    z: 0,
    headingRad: 0,
    pitchRad: 0,
    bankRad: 0,
    airspeedMs: 0,
    altitudeAglM: 0,
    loadFactor: 0,
    integrity: 0,
    throttle: 0,
  };
}

/**
 * The handful of lessons worth leading with.
 *
 * Ordered by cost, and capped: a debrief that lists twelve faults teaches none of them. The cap is
 * the point — the player should leave with one thing to do differently.
 */
export function buildLessons(
  objectives: readonly ObjectiveStatus[],
  missiles: readonly MissileAnalysis[],
  meanIngressAltitudeM: number,
  score: MissionScore | null,
): string[] {
  const lessons: string[] = [];

  for (let i = 0; i < objectives.length; i += 1) {
    const objective = objectives[i];
    if (objective.primary && objective.state !== 'complete') {
      lessons.push(`Primary objective not met: ${objective.description}.`);
    }
  }

  const hits = missiles.filter((m) => m.verdict === EvasionVerdict.Hit);
  if (hits.length > 0) lessons.push(hits[0].explanation);

  const lucky = missiles.filter((m) => m.verdict === EvasionVerdict.Incidental);
  if (hits.length === 0 && lucky.length > 0 && lucky.length === missiles.length) {
    lessons.push(
      'Every missile missed without you defeating it. That will not hold against a better crew.',
    );
  }

  if (meanIngressAltitudeM > SCORING.poorIngressAltM * 0.6) {
    lessons.push(
      `Your mean ingress altitude was ${Math.round(meanIngressAltitudeM)} m. ` +
        `Radar horizon scales with height — flying lower is the cheapest defence you have.`,
    );
  }

  if (score !== null && lessons.length < DEBRIEF.maxLessons) {
    // Skip the generic "a primary objective was not completed": the specific lines above have
    // already named which ones, and a debrief that repeats itself in vaguer words reads as noise
    // and pushes the useful lesson off the bottom of a capped list.
    const specific = score.expertShortfalls.find(
      (shortfall) => !shortfall.startsWith('a primary objective'),
    );
    if (specific !== undefined && !lessons.includes(specific)) lessons.push(specific);
  }

  return lessons.slice(0, DEBRIEF.maxLessons);
}

/** Assemble the whole report. */
export function buildDebrief(params: {
  missionTitle: string;
  success: boolean;
  outcomeReason: string;
  score: MissionScore | null;
  objectives: readonly ObjectiveStatus[];
  recorder: FlightRecorder;
  elapsedS: number;
}): DebriefReport {
  const missiles = analyseEngagements(params.recorder);
  const meanIngressAltitudeM = params.recorder.meanAltitudeAglM();
  return {
    missionTitle: params.missionTitle,
    success: params.success,
    outcomeReason: params.outcomeReason,
    score: params.score,
    objectives: params.objectives,
    missiles,
    elapsedS: params.elapsedS,
    meanIngressAltitudeM,
    peakLoadFactor: params.recorder.peakLoadFactor(),
    lessons: buildLessons(params.objectives, missiles, meanIngressAltitudeM, params.score),
  };
}
